import {
  login,
  logout,
  getInfo,
  getInfo_,
  getFundInfo,
  queryCustomerInfo,
} from "@user-center/api/user";
import { loginWithEmailVerificationCode } from "@user-center/api/common";
import { queryUnreadNotificationsCount } from "@user-center/api/erp";
import { getMallUserInfo } from "@user-center/api/mall";
import { storeData } from "@user-center/utils/localService.ts";
import { queryDealerShop } from "@user-center/api/common.ts";
import { getToken, setToken, removeToken, isErpUser } from "@user-center/utils/auth";

import { isMallApp,isMallAppApi, setMallAppToken } from "@user-center/utils/qiankun";
import lodash, { flatMapDeep, get } from "lodash-es";
import type { ActionContext, ActionTree, Commit, MutationTree } from "vuex";
import { queryCustomerInfoByMemberCodeApi } from "@user-center/api/sharedInventory";

export interface FundInfoType {
  id: string;
  userId: string;
  userType: string;
  userName: string;
  userBankName: string;
  userBankAccount: string;
  userAlipayAccount: string;
  depositReceived: string;
  totalDeductionsAmount: number;
  totalCollectionAmount: number;
  totalTransferOutAmount: number;
  totalTransferInAmount: number;
  totalRechargeAmount: number;
  totalWithdrawalAmount: number;
  fundBalance: number;
  commission: string;
}

export interface CustomerInfoType {
  id: string;
  userId: string;
  memberCode: string;
  nickname: string;
  autoPay: number;
  autoPayAmount: number;
  wxSubscribe: number;
}

export interface RoleType {
  code: string;
  id: string;
  name: string;
  remark: string;
  status: number;
}
export interface MenuType {
  id: string;
  name: string;
  code: string;
  parentId: string;
  remark?: string;
  level: number;
  sequence: number;
  url: string;
  createTime: string;
  frontPath?: string;
  updateTime?: string;
  status: number;
  menuButtonList?: string;
  children?: Array<MenuType>;
}
export interface UserStateType {
  mallUserInfo?: any;
  memberCode?: string;
  token: string;
  account: string;
  avatar: string;
  id: string;
  isTopOrganization: boolean;
  sumCustomerWithhold: number;
  fundInfo: FundInfoType;
  customerInfo: CustomerInfoType;
  roles: Array<RoleType>;
  allMenus: Array<MenuType>;
  roleMenus: Array<MenuType>;
  treeMenus: Array<MenuType>;
  roleAndRouteMenus: Array<MenuType>;
  menuCodeTipMap: any;
  mainUserId?: string;
  name?: string;
  realName?: string;
  hadModifyPwd: number;
  companyInfo: any;

  isGroupAdmin?: number
  subGroupId?: string
  groupName?: string
}

function getBrowserStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  if (typeof localStorage !== "undefined") {
    return localStorage;
  }
  return null;
}

const getTokenData = getToken();
export const emptyUserFund: FundInfoType = {
  id: "",
  userId: "",
  userType: "",
  userName: "",
  userBankName: "",
  userBankAccount: "",
  userAlipayAccount: "",
  depositReceived: "",
  totalDeductionsAmount: 0,
  totalCollectionAmount: 0,
  totalTransferOutAmount: 0,
  totalTransferInAmount: 0,
  totalRechargeAmount: 0,
  totalWithdrawalAmount: 0,
  fundBalance: 0,
  commission: "",
};
export const emptyUserCustomer: CustomerInfoType = {
  id: "",
  userId: "",
  memberCode: "",
  nickname: "",
  autoPay: 0,
  autoPayAmount: 0,
  wxSubscribe: 0,
};

export const initState: UserStateType = {
  token: typeof getTokenData == "string" ? getTokenData : "",
  account: "",
  avatar: "",
  id: "",
  isTopOrganization: false,
  sumCustomerWithhold: 0,
  fundInfo: lodash.cloneDeep(emptyUserFund),
  customerInfo: lodash.cloneDeep(emptyUserCustomer),
  mallUserInfo: {},
  roles: [],
  allMenus: [],
  roleMenus: [],
  treeMenus: [],
  roleAndRouteMenus: [],
  menuCodeTipMap: {},
  name: "",
  realName: "",
  mainUserId: "",
  memberCode: "",
  hadModifyPwd: 1,
  companyInfo: {},
  isGroupAdmin: 0,
  subGroupId: "",
  groupName: "",
};
export const state: UserStateType = {
  ...lodash.cloneDeep(initState),
};

const mutations: MutationTree<UserStateType> = {
  CLEAR_USER: (state: UserStateType): void => {
    removeToken();

    state = Object.assign(state, initState);
  },
  SET_USER: (state: UserStateType, setUser: Partial<UserStateType>): void => {
    if (setUser.token) {
      setToken(setUser.token);
    }
    if (setUser.account && setUser.account != state.account) {
      getBrowserStorage()?.setItem("lastLoginUsername", setUser.account);
    }
    if (!setUser.roles) {
      setUser.roles = [];
    }
    state = Object.assign(state, setUser);
  },
  SET_TOKEN: (state: UserStateType, token: string): void => {
    state.token = token;
  },
  SET_FUND_INFO: (state: UserStateType, fundInfo: FundInfoType): void => {
    state.fundInfo = fundInfo;
  },
  SET_CUSTOMER_INFO: (
    state: UserStateType,
    customerInfo: CustomerInfoType
  ): void => {
    state.customerInfo = customerInfo;
  },
  SET_SUM_CUSTOMER_WITH_HOLD: (
    state: UserStateType,
    sumCustomerWithhold: number
  ): void => {
    state.sumCustomerWithhold = sumCustomerWithhold || 0;
  },
  SET_ALL_MENUS: (state: UserStateType, menus: MenuType[]): void => {
    state.allMenus = menus || [];
  },
  SET_TREE_MENUS: (state: UserStateType, menus: MenuType[]): void => {
    state.treeMenus = menus || [];
  },
  SET_ROLE_MENUS: (state: UserStateType, menus: MenuType[]): void => {
    state.roleMenus = menus || [];
  },
  SET_ROLE_AND_ROUTE_MENUS: (state: UserStateType, menus: MenuType[]): void => {
    let setMenus = menus || [];
    setMenus = setMenus.map((o) => {
      let setUrlMap = {};

      flattenTree([o]).map((urlObj) => {
        if (urlObj.frontPath) {
          setUrlMap[urlObj.frontPath] = true;
        }
      });
      o.urlMap = setUrlMap;
      return o;
    });
    state.roleAndRouteMenus = setMenus;
  },
  SET_MENU_COD_TIP_MAP: (state: UserStateType, menuCodeTipMap: any): void => {
    state.menuCodeTipMap = menuCodeTipMap || {};
  },
  SET_MALL_USER_INFO: (state: UserStateType, mallUserInfo: any): void => {
    state.mallUserInfo = mallUserInfo || {};
  },
  SET_COMPANY_INFO: (state: UserStateType, companyInfo: any): void => {
    state.companyInfo = companyInfo || {};
  }
};

function flattenTree(tree) {
  return flatMapDeep(tree, (node) => {
    const children = node.children || [];
    return [node, ...flattenTree(children)];
  });
}

const setUserInfoFilter = function (
  // 公用获取登录信息方法
  response: any,

  token: string
): UserStateType {
  const { data } = response;

  if (!data) {
    throw "验证用户信息错误,请重新登陆";
  }
  data as UserStateType;
  const setUser: UserStateType = {
    ...data,
    token: token,
  };
  return setUser;
};

const actions: ActionTree<UserStateType, any> = {
  // user login

  login(
    store: ActionContext<UserStateType, any>,
    userInfo: { username: string; password: string; shopId: number | string,phone: string|null,smsCode: string|null }
  ): Promise<any> {
    store.commit("CLEAR_USER");
    let { username, password, shopId,phone,smsCode } = userInfo;
    return new Promise(async (resolve, reject) => {
      if (!shopId) {
        try {
          const data = {
            shopBackUrl:
              window.location.host.indexOf("localhost") >= 0
                ? `http://xjhy.shopmember-test.omszt.com:7888`
                : `http://${window.location.host}`,
          };
          const res: any = await queryDealerShop(data);
          if (res.data && res.data.id) {
            shopId = res.data.id;
            const loginState: any = {};
            res.data.title = `${res.data.title}`;
            loginState.systemInfo = res.data;
            localStorage.setItem(
              storeData.StoreInformation,
              JSON.stringify(res.data)
            );
            localStorage.setItem("shopId", res.data.id);
            document.title = loginState.systemInfo.title as string;
            // 修改页签图标
            const link: any = document.getElementsByClassName("Home-link");
            if (link[0]) {
              link[0].rel = "icon"; //设置rel类型
              console.log(loginState.systemInfo.logoUrl);
              link[0].href = loginState.systemInfo.logoUrl; //设置href链接
              document.head.appendChild(link[0]); //输出修改
            }
          }
        } catch (err) {
          console.log(err);
        }
      }
      login({
        phone,
        smsCode,
        username: username.trim(),
        password: password,
        shopId: shopId || localStorage.getItem("shopId"),
      })
        .then((response: { data: any; token: string } | any) => {
          const { data, token } = response;
          getInfo_()
            .then((response) => {
              const userState: UserStateType = setUserInfoFilter(
                response,
                token
              );
              store.commit("SET_USER", userState);
              return resolve(userState);
            })
            .catch((error) => {
              reject(error);
            });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  loginByEmailCode(
    store: ActionContext<UserStateType, any>,
    userInfo: { email: string; code: string; shopId: number | string }
  ): Promise<any> {
    store.commit("CLEAR_USER");
    let { email, code, shopId } = userInfo;

    return new Promise(async (resolve, reject) => {
      if (!shopId) {
        try {
          const data = {
            shopBackUrl:
              window.location.host.indexOf("localhost") >= 0
                ? `http://xjhy.shopmember-test.omszt.com:7888`
                : `http://${window.location.host}`,
          };
          const res: any = await queryDealerShop(data);
          if (res.data && res.data.id) {
            shopId = res.data.id;
            localStorage.setItem(
              storeData.StoreInformation,
              JSON.stringify(res.data)
            );
            localStorage.setItem("shopId", res.data.id);
            document.title = `${res.data.title || ""}`;
            const link: any = document.getElementsByClassName("Home-link");
            if (link[0]) {
              link[0].rel = "icon";
              link[0].href = res.data.logoUrl;
              document.head.appendChild(link[0]);
            }
          }
        } catch (err) {
          console.log(err);
        }
      }

      loginWithEmailVerificationCode({
        param: {
          email: email.trim(),
          code: code.trim(),
          shopId: shopId || localStorage.getItem("shopId"),
        },
      })
        .then(async (response: any) => {
          const token =
            response?.token
            || response?.data?.token
            || getToken()
            || "";

          try {
            const userResponse = await getInfo_();
            const userState: UserStateType = setUserInfoFilter(
              userResponse,
              typeof token === "string" ? token : ""
            );
            store.commit("SET_USER", userState);
            resolve(userState);
          } catch (error) {
            reject(error);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // 账号信息查询
  getInfo(store: ActionContext<UserStateType, any>) {
    return new Promise((resolve, reject) => {
      const lastLoginUserName = localStorage.getItem("lastLoginUsername");
      getInfo_()
        .then(async (response) => {
          const getTokenData = getToken();
          const userState: UserStateType = setUserInfoFilter(
            response,
            typeof getTokenData == "string" ? getTokenData : ""
          );
          store.commit("SET_USER", userState);
          await store.dispatch("getCompanyInfo");
          if (isMallApp()) {
            await store.dispatch("getMallUserInfo");
          }
          if(!isMallApp()&&isMallAppApi()){
            await store.dispatch("getMallUserInfo");
          }
          return resolve(userState);
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  },
  // 账户资金信息查询
  getFundInfo(store: ActionContext<UserStateType, any>) {
    return new Promise((resolve, reject) => {
      getFundInfo(state.id)
        .then((response: any) => {
          const { data } = response;

          store.commit("SET_FUND_INFO", data);
          resolve(data);
        })
        .catch((error) => {
          store.commit("SET_FUND_INFO", lodash.cloneDeep(emptyUserFund));
          reject(error);
        });
    });
  },
  // 账户自定义信息查询
  getCustomerInfo(store: ActionContext<UserStateType, any>) {
    store.commit("SET_CUSTOMER_INFO", lodash.cloneDeep(emptyUserCustomer));
    return new Promise((resolve, reject) => {
      queryCustomerInfo(state.id)
        .then((response: any) => {
          const { data } = response;

          store.commit("SET_CUSTOMER_INFO", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // 货主信息查询
  getCompanyInfo(store: ActionContext<UserStateType, any>) {
    return new Promise((resolve, reject) => {
      if (!state.memberCode) {
        resolve({});
        return
      }
      queryCustomerInfoByMemberCodeApi({
        memberCode: state.memberCode,
        dealerShopId: localStorage.getItem('shopId'),
      }).then((response: any) => {
        const { data } = response;
        store.commit("SET_COMPANY_INFO", data);
        resolve(data);
      }).catch((error) => {
        reject(error);
      })
    })
  },

  // 用户退出
  logout(store: ActionContext<UserStateType, any>) {
    return new Promise((resolve, reject) => {
      logout()
        .then(() => {
          store.commit("CLEAR_USER");

          setTimeout(resolve, 500);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // 删除token方法
  resetToken(store: ActionContext<UserStateType, any>) {
    return new Promise((resolve) => {
      store.commit("CLEAR_USER");

      resolve(() => {
        console.log("删除token成功");
      });
    });
  },
  async getMenuTipMap(store: ActionContext<UserStateType, any>) {
    let menuCodeTipMap = {
      WorkOrderService: 0,
      PlatformOrderWorkOrder: 0,
      NewPlatformOrderWorkOrder: 0,
    };
    if (isErpUser(state)) {
      let workOrderErrRes: any = await queryUnreadNotificationsCount();
      if (workOrderErrRes.data) {
        menuCodeTipMap.PlatformOrderWorkOrder =
          workOrderErrRes.data.pendingWorkOrderQty;
          menuCodeTipMap.WorkOrderService =
            workOrderErrRes.data.pendingWorkOrderQty;
          menuCodeTipMap.NewPlatformOrderWorkOrder =
            workOrderErrRes.data.pendingWorkOrderQty;
      }
      store.commit("SET_MENU_COD_TIP_MAP", menuCodeTipMap);
    }
  },
  async getMallUserInfo(store: ActionContext<UserStateType, any>) {
    return new Promise(async (resolve) => {
      let memberCode = get(store, "state.memberCode");
      if (!memberCode) {
        store.commit("SET_MALL_USER_INFO", {});
        resolve({});
        return;
      }
      try {
        let collectionMemberCode = get(
          store,
          "state.mallUserInfo.collectionMemberCode"
        );
        if (collectionMemberCode == memberCode) {
          resolve(get(store, "state.mallUserInfo"));
          return;
        }
        let mallUserResData: any = await getMallUserInfo({
          param: {
            thirdParty: 5,
            thirdPartyType: "SZT",
            thirdPartyId: memberCode,
          },
        });
        if (mallUserResData.token) {
          setMallAppToken(mallUserResData.token);
          store.commit("SET_MALL_USER_INFO", mallUserResData.data);
          resolve(mallUserResData.data);
        } else {
          throw "tokenerr";
        }

        console.log("mallUserResData", mallUserResData);
      } catch (e) {
        resolve({});
        store.commit("SET_MALL_USER_INFO", {});
      }
    });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
